import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Avatar, Box, Card, CardContent, Chip, IconButton, ListItemText, Menu, MenuItem, Stack,
  Tooltip, Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FlagIcon from '@mui/icons-material/Flag';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import type { BoardCard, Stage } from '../../api/types';
import { STAGE_ORDER } from '../../api/types';
import { formatCurrency, formatDate } from '../common/format';
import { UserText } from '../common/Common';
import { useThemeMode } from '../../theme/ThemeModeProvider';

export type Density = 'compact' | 'comfortable';

/** Days in stage before a deal is worth chasing. Only open stages age — a closed deal sitting in
 *  Closed is not rotting, it is finished. */
const AGEING_AMBER_DAYS = 14;
const AGEING_RED_DAYS = 30;

export function OpportunityCard({
  card, stage, draggable, density = 'compact', onMoveTo,
}: {
  card: BoardCard;
  stage: Stage;
  draggable: boolean;
  density?: Density;
  onMoveTo: (card: BoardCard, to: Stage) => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { stageColors } = useThemeMode();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    disabled: !draggable,
    data: { card, stage },
  });

  const openMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const move = (to: Stage) => {
    setMenuAnchor(null);
    onMoveTo(card, to);
  };

  const compact = density === 'compact';
  const outcomeColor = card.closedOutcome === 'lost' ? stageColors.lost : stageColors.won;

  const ageing = stage !== 'closed' && card.daysInStage !== null
    ? card.daysInStage >= AGEING_RED_DAYS ? 'error'
      : card.daysInStage >= AGEING_AMBER_DAYS ? 'warning' : null
    : null;

  // When dragging is off (phone layout), don't advertise drag semantics at all. dnd-kit's
  // attributes include aria-disabled when disabled, which would make the card — and the ⋮ button
  // inside it — read as disabled to assistive tech and to Playwright.
  const dragProps = draggable ? { ...attributes, ...listeners } : {};

  const ownerAvatar = card.ownerName ? (
    <Tooltip title={card.ownerName}>
      <Avatar
        sx={{
          width: compact ? 16 : 20, height: compact ? 16 : 20,
          fontSize: compact ? 9 : 10, bgcolor: 'secondary.main',
        }}
        data-testid={`opp-card-owner-${card.id}`}
      >
        {card.ownerName.trim().charAt(0).toUpperCase()}
      </Avatar>
    </Tooltip>
  ) : null;

  const partnerIcon = card.partnerName ? (
    <Tooltip title={card.partnerName}>
      <HandshakeIcon
        sx={{ fontSize: 16, color: 'text.secondary' }}
        data-testid={`opp-card-partner-${card.id}`}
      />
    </Tooltip>
  ) : null;

  const ageingChip = ageing ? (
    <Tooltip title={t('board.daysInStage', { count: card.daysInStage ?? 0 })}>
      <Chip
        size="small"
        icon={<HourglassBottomIcon />}
        label={`${card.daysInStage}d`}
        color={ageing}
        variant={ageing === 'error' ? 'filled' : 'outlined'}
        sx={compact ? { height: 20, '& .MuiChip-label': { px: 0.75 } } : undefined}
        data-testid={`opp-card-ageing-${card.id}`}
      />
    </Tooltip>
  ) : null;

  const dueChip = card.nextStepDueDate ? (
    <Chip
      size="small"
      icon={<FlagIcon />}
      label={formatDate(card.nextStepDueDate)}
      color={card.isNextStepOverdue ? 'error' : 'default'}
      variant={card.isNextStepOverdue ? 'filled' : 'outlined'}
      sx={compact ? { height: 20, '& .MuiChip-label': { px: 0.75 } } : undefined}
      data-testid={`opp-card-due-${card.id}`}
    />
  ) : null;

  const outcomeChip = card.closedOutcome ? (
    <Chip
      size="small"
      label={t(`outcome.${card.closedOutcome}`)}
      sx={{
        color: outcomeColor,
        borderColor: outcomeColor,
        ...(compact ? { height: 20, '& .MuiChip-label': { px: 0.75 } } : {}),
      }}
      variant="outlined"
    />
  ) : null;

  return (
    <>
      <Card
        ref={setNodeRef}
        data-testid={`opp-card-${card.id}`}
        data-density={density}
        onClick={() => navigate(`/opportunities/${card.id}`)}
        style={{ transform: CSS.Translate.toString(transform) }}
        sx={{
          cursor: 'pointer',
          opacity: isDragging ? 0.4 : 1,
          '&:hover': {
            boxShadow: (theme) =>
              theme.palette.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.10)',
            transform: 'translateY(-1px)',
          },
        }}
        {...dragProps}
      >
        {compact ? (
          // Two rows: title + value, then everything else as icons and mini chips. Roughly half
          // the height of the comfortable card, which is the whole point of the density toggle.
          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, flexGrow: 1 }} noWrap title={card.title}>
                <UserText>{card.title}</UserText>
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {formatCurrency(card.estimatedValue, card.currency)}
              </Typography>
              <IconButton
                size="small"
                sx={{ p: 0.25 }}
                onClick={openMenu}
                aria-label={t('board.cardActions')}
                data-testid={`opp-card-menu-${card.id}`}
              >
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0, flexGrow: 1 }}>
                <UserText>{card.customerName}</UserText>
              </Typography>
              {ownerAvatar}
              {ageingChip}
              {dueChip}
              {card.closedOutcome ? outcomeChip : partnerIcon}
            </Stack>
          </CardContent>
        ) : (
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="flex-start" spacing={0.5}>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="h3" noWrap title={card.title}>
                  <UserText>{card.title}</UserText>
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <UserText>{card.customerName}</UserText>
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={openMenu}
                aria-label={t('board.cardActions')}
                data-testid={`opp-card-menu-${card.id}`}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
              {formatCurrency(card.estimatedValue, card.currency)}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {card.ownerName && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {ownerAvatar}
                  <Typography variant="caption" color="text.secondary">{card.ownerName}</Typography>
                </Stack>
              )}
              {card.partnerName && (
                <Chip
                  size="small"
                  icon={<HandshakeIcon />}
                  label={card.partnerName}
                  variant="outlined"
                  data-testid={`opp-card-partner-${card.id}`}
                />
              )}
              {ageingChip}
              {dueChip}
              {outcomeChip}
            </Stack>
          </CardContent>
        )}
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>{t('board.moveToStage')}</Typography>
        {STAGE_ORDER.filter((s) => s !== stage).map((s) => (
          <MenuItem key={s} onClick={() => move(s)} data-testid={`move-to-${s}-${card.id}`}>
            <ListItemText>{t(`stage.${s}`)}</ListItemText>
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            navigate(`/customers/${card.customerId}`);
          }}
        >
          <ListItemText>{t('board.openCustomer')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
